"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck2, Microscope, FileText, MessageSquareText, CalendarCheck2, LifeBuoy, ArrowRight, Clock, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const steps = [{
  stepNumber: "01",
  title: "Submission Review",
  subtitle: "Within 2-4 business hours",
  description: "Our technical sales team logs your instrument requirements, application parameters, sample matrices, and target laboratory compliance standards.",
  icon: FileCheck2,
  highlights: ["Application scope check", "Direct specialist assignment"]
}, {
  stepNumber: "02",
  title: "Expert Analysis",
  subtitle: "Detailed technical evaluation",
  description: "Application specialists evaluate optical configurations, accessories, chemometric needs, and qualification documentation tailored to your workflow.",
  icon: Microscope,
  highlights: ["Feasibility verification", "Hardware & software scoping"]
}, {
  stepNumber: "03",
  title: "Quote Delivery",
  subtitle: "Formal proposal & consultation",
  description: "You receive an itemized proposal with hardware specifications, qualification packages, delivery lead times, and warranty coverage details.",
  icon: FileText,
  highlights: ["Clear pricing breakdown", "Optional consultation call"]
}];
const conversionPaths = [{
  title: "Discuss Your Application",
  description: "Speak directly with an analytical chemist to verify method feasibility, sample suitability, and regulatory considerations.",
  icon: MessageSquareText,
  href: "/DiscussYourApplicationPage",
  cta: "Schedule Application Discussion",
  badge: "Technical Advice"
}, {
  title: "Book a Demo",
  description: "Experience live or virtual demonstrations with your own sample types on NIR, Raman, or powder testing instrumentation.",
  icon: CalendarCheck2,
  href: "/BookADemoPage",
  cta: "Reserve a Demonstration",
  badge: "Live Testing"
}, {
  title: "Support Request",
  description: "Need service, preventive maintenance, instrument calibration, or qualification support for your existing system?",
  icon: LifeBuoy,
  href: "/SupportRequestPage",
  cta: "Submit Support Ticket",
  badge: "Service & Care"
}];
export default function QuoteNextSteps() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="quotenextstepsandconversionpaths" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Process Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Clear & Transparent Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            What Happens After You Request a Quote
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default">
            Our specialized application engineers review every request to provide precise, compliant, and actionable proposals for your laboratory.
          </p>
        </div>

        {/* 3-Step Process Flow */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative mb-16 md:mb-24">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <motion.div key={step.stepNumber} data-index={i} variants={itemVariants} className="relative flex flex-col h-full">
                <div className="h-full rounded-xl border border-border bg-card p-6 md:p-7 shadow-xs flex flex-col justify-between transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                  <div>
                    {/* Top Row: Number and Icon */}
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <span className="text-3xl font-extrabold text-primary font-default">
                        {step.stepNumber}
                      </span>
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground">
                        <IconComponent className="size-5" />
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
                      <Clock className="size-3.5" />
                      <span>{step.subtitle}</span>
                    </div>

                    <h3 className="text-xl font-bold text-card-foreground mb-3 font-default">
                      {step.title}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {step.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-5 border-t border-border/80 space-y-2">
                    {step.highlights.map((highlight, hIndex) => <div key={hIndex} className="flex items-center gap-2 text-xs text-card-foreground">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>{highlight}</span>
                      </div>)}
                  </div>
                </div>

                {/* Connector Arrow for Desktop */}
                {i < steps.length - 1 && <div aria-hidden="true" className="hidden lg:flex absolute -right-4 top-1/3 -translate-y-1/2 z-10 size-8 rounded-full bg-background border border-border items-center justify-center text-muted-foreground shadow-xs">
                    <ArrowRight className="size-4 text-primary" />
                  </div>}
              </motion.div>;
        })}
        </motion.div>

        {/* Commitment Banner */}
        <div className="rounded-xl bg-secondary/70 border border-border p-6 md:p-8 mb-16 md:mb-20">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="size-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <ShieldCheck className="size-6" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-secondary-foreground font-default">
                  Regulatory Compliance & Integrity Assurance
                </h4>
                <p className="text-sm text-muted-foreground mt-0.5 font-default">
                  Every instrument specification includes complete traceability and qualification guidance for regulated manufacturing environments.
                </p>
              </div>
            </div>
            <div className="shrink-0 w-full sm:w-auto">
              <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                <Link to="/ContactPage">
                  Questions? Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Additional Conversion Paths */}
        <div>
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground font-default">
              Other Ways We Can Help Today
            </h3>
            <p className="mt-2 text-sm text-muted-foreground font-default">
              Explore immediate support options, live instrument demonstrations, or targeted feasibility consults.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {conversionPaths.map((path, i) => {
            const IconComp = path.icon;
            return <motion.div key={path.title} data-index={i} variants={itemVariants} className="flex">
                  <Card className="w-full flex flex-col justify-between border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                    <CardHeader>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="size-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center">
                          <IconComp className="size-5" />
                        </div>
                        <Badge variant="outline">{path.badge}</Badge>
                      </div>
                      <CardTitle className="text-lg font-bold font-default">
                        {path.title}
                      </CardTitle>
                      <CardDescription className="text-sm leading-relaxed mt-1 font-default">
                        {path.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {/* Empty padding container as specified in card architecture */}
                    </CardContent>
                    <CardFooter className="pt-2">
                      <Button asChild variant="secondary" className="w-full justify-between group">
                        <Link to={path.href}>
                          <span>{path.cta}</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}